import { generateDatadogTrackingProps } from 'toolkit/environment/environment/userMonitoring.util';
import { useMemo } from 'react';
import {
  mockSectionData,
  resolveEngagementFunnelStepSubCopy,
  type MockSectionCustomerInput,
  type SectionId,
  type SectionMetaDto,
  WHAT_MEMBERS_NEED_FULFILLMENT_STEPS,
} from '@act/shared/data-transfer-objects';

export interface InsightItem {
  heading: string;
  body: string;
  citationUrl?: string;
  citationLabel?: string;
}

export interface SectionInsights {
  clinical?: InsightItem;
  financial?: InsightItem;
}

/**
 * Shared section renderer (Rules.md #27). Every output surface renders
 * sections through this component so they stay in visual + content sync.
 */
export type SectionRenderMode =
  | "builder-preview"
  | "preview"
  | "published"
  | "pdf"
  | "slide";

interface Props {
  section: SectionMetaDto;
  reportSection: Pick<
    ReportSectionRow,
    | 'sectionId'
    | 'calloutEnabled'
    | 'narrative'
    | 'callout'
    | 'calloutTitle'
    | 'dataSnapshot'
  > & { insights?: SectionInsights | null };
  customer: MockSectionCustomerInput;
  reportingPeriod: string;
  mode: SectionRenderMode;
}

interface ReportSectionRow {
  sectionId: string;
  calloutEnabled: boolean;
  narrative: string | null;
  callout: string | null;
  calloutTitle?: string | null;
  dataSnapshot: Record<string, unknown> | null;
}

export function SectionRenderer({
  section,
  reportSection,
  customer,
  reportingPeriod,
  mode,
}: Props) {
  if (mode === "pdf" || mode === "slide") {
    throw new Error(
      `SectionRenderer mode "${mode}" is not implemented yet (arrives in Phase 5).`,
    );
  }

  const data = useMemo(() => {
    if (reportSection.dataSnapshot) return reportSection.dataSnapshot;
    return mockSectionData(
      reportSection.sectionId as SectionId,
      customer,
      reportingPeriod,
    );
  }, [reportSection.dataSnapshot, reportSection.sectionId, customer, reportingPeriod]);

  // Insights: prefer explicit authoring on reportSection; fall back to what
  // mock/snapshot data carries. Backward-compatible for old publish events.
  const insights: SectionInsights | null =
    reportSection.insights ??
    (data.insights as SectionInsights | undefined) ??
    null;

  const showInsights =
    section.supportsCallout &&
    reportSection.calloutEnabled &&
    !!insights &&
    (insights.clinical || insights.financial);

  const chrome = mode === "builder-preview";
  const hosted = mode === "preview" || mode === "published";

  // In hosted mode the hero renders at the top of PreviewReadingColumn as a
  // full-bleed navy block. Skip rendering it again inline.
  if (hosted && reportSection.sectionId === "hero") {
    return null;
  }

  if (hosted) {
    return (
      <article className="space-y-6">
        <header>
          <span className="report-eyebrow">{section.title}</span>
          <hr className="report-divider" />
          {typeof data.headline === "string" && data.headline.length > 0 ? (
            <h2 className="report-title">{String(data.headline)}</h2>
          ) : (
            <h2 className="report-title">{section.title}</h2>
          )}
          {typeof data.tagline === "string" && data.tagline.length > 0 && (
            <p className="report-lede">{String(data.tagline)}</p>
          )}
        </header>

        <div className="text-[15px] leading-relaxed text-[color:var(--reema-midnight-100)]">
          <SectionBody sectionId={reportSection.sectionId} data={data} />
        </div>

        {showInsights && <InsightPair insights={insights!} />}
      </article>
    );
  }

  return (
    <article
      className={
        chrome
          ? "space-y-3 rounded-md border bg-background p-4"
          : "space-y-4 rounded-lg border bg-background p-6"
      }
    >
      <header className="space-y-1">
        {chrome && (
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {customer.name} · Section preview
          </p>
        )}
        <h3 className={chrome ? "text-lg font-semibold" : "text-xl font-semibold"}>
          {section.title}
        </h3>
      </header>

      <SectionBody sectionId={reportSection.sectionId} data={data} />

      {showInsights && <InsightPair insights={insights!} />}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Per-section data body.
// ---------------------------------------------------------------------------

function SectionBody({
  sectionId,
  data,
}: {
  sectionId: string;
  data: Record<string, unknown>;
}) {
  switch (sectionId) {
    case "hero": {
      const tiles =
        (data.kpiTiles as Array<{ label: string; value: string }>) ?? [];
      return (
        <div className="space-y-4 rounded-md bg-secondary/40 p-5">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {String(data.payer ?? "")} · {String(data.reportingPeriod ?? "")}
            </p>
            <p className="mt-1 text-2xl font-semibold">
              {String(data.customerName ?? "")}
            </p>
            {data.headline ? (
              <p className="mt-2 text-lg font-medium leading-snug">
                {String(data.headline)}
              </p>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">
                {String(data.tagline ?? "")}
              </p>
            )}
          </div>
          {tiles.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
              {tiles.map((t) => (
                <KpiTile key={t.label} label={t.label} value={t.value} />
              ))}
            </div>
          )}
        </div>
      );
    }

    case "engagement-story": {
      const funnel =
        (data.funnel as Array<{
          label: string;
          value: number;
          subCopy?: string;
        }>) ?? [];
      const denoms =
        (data.denominators as Array<{
          label: string;
          value: string;
          hint?: string;
          variant?: 'engagement-rate' | 'conversion-rate';
        }>) ?? [];
      const trend =
        (data.trend as Array<{
          month: string;
          engaged: number;
          interacted: number;
        }>) ?? [];
      return (
        <div className="space-y-5">
          {trend.length > 0 && <EngagementTrend series={trend} />}
          {funnel.length > 0 && (
            <Funnel
              steps={funnel.map(step => ({
                ...step,
                subCopy:
                  resolveEngagementFunnelStepSubCopy(step.label, funnel) ??
                  step.subCopy,
              }))}
            />
          )}
          {denoms.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--reema-coral-100,#e0654a)]">
                Engagement rate depends on the denominator
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {denoms.map(d => (
                  <div
                    key={d.label}
                    className="rounded-md border bg-card p-3"
                  >
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {d.label}
                    </p>
                    <p className="mt-1 text-2xl font-semibold">{d.value}</p>
                    {d.hint ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {d.hint}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          )}
          {Boolean(data.whyDenominator) && (
            <p className="rounded-md bg-muted/40 p-3 text-xs italic text-muted-foreground">
              Why the denominator matters — {String(data.whyDenominator)}
            </p>
          )}
        </div>
      );
    }


    case "vbp-engagement": {
      const funnel =
        (data.funnel as Array<{ label: string; value: number }>) ?? [];
      const growth = asNumberArray(data.sixMonthGrowth);
      return (
        <div className="space-y-3">
          {growth.length > 0 && (
            <BarSpark values={growth} label="Engaged members — last 6 months" />
          )}
          {funnel.length > 0 && <Funnel steps={funnel} />}
        </div>
      );
    }

    case "who-were-reaching": {
      const geo = (data.geo as Array<{ label: string; pct: number }>) ?? [];
      const age = (data.age as Array<{ label: string; pct: number }>) ?? [];
      const re =
        (data.raceEthnicity as Array<{ label: string; pct: number }>) ?? [];
      return (
        <div className="grid gap-4 md:grid-cols-3">
          <BreakdownList title="County" rows={geo} />
          <BreakdownList title="Age" rows={age} />
          <BreakdownList title="Race / Ethnicity" rows={re} />
        </div>
      );
    }

    case "what-members-need": {
      const needs =
        (data.topNeeds as Array<{ label: string; count: number }>) ?? [];
      const trend =
        (data.identifiedVsFulfilled as Array<{
          label: string;
          identified: number;
          fulfilled: number;
        }>) ?? [];
      return (
        <div className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-4">
            {WHAT_MEMBERS_NEED_FULFILLMENT_STEPS.map((s) => (
              <div key={s.step} className="rounded-md border bg-card p-3">
                <p className="text-xs font-semibold text-primary">
                  Step {s.step}
                </p>
                <p className="mt-0.5 text-sm font-medium">{s.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
          {needs.length > 0 && (
            <ol className="space-y-1 text-sm">
              {needs.map((n) => (
                <li
                  key={n.label}
                  className="flex items-center justify-between border-b border-dashed py-1"
                >
                  <span>{n.label}</span>
                  <span className="text-muted-foreground">
                    {n.count.toLocaleString()} members
                  </span>
                </li>
              ))}
            </ol>
          )}
          {trend.length > 0 && (
            <div>
              <p className="mb-1 text-xs text-muted-foreground">
                Identified vs Fulfilled (last 4 quarters)
              </p>
              <div className="space-y-1">
                {trend.map((q) => {
                  const pct = Math.round((q.fulfilled / q.identified) * 100);
                  return (
                    <div key={q.label}>
                      <div className="flex justify-between text-xs">
                        <span>{q.label}</span>
                        <span className="text-muted-foreground">
                          {q.fulfilled.toLocaleString()} /{" "}
                          {q.identified.toLocaleString()} ({pct}%)
                        </span>
                      </div>
                      <ProgressBar value={pct} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }

    case "connections-that-matter": {
      const cbos =
        (data.topCbos as Array<{ label: string; count: number }>) ?? [];
      const gov =
        (data.topGov as Array<{ label: string; count: number }>) ?? [];
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <RankedList title="Top CBO connections" rows={cbos} />
          <RankedList title="Top government-program connections" rows={gov} />
        </div>
      );
    }

    case "quality-measure-projections": {
      const measures =
        (data.measures as Array<{
          name: string;
          current: number;
          projected: number;
          target: number;
          delta: number;
        }>) ?? [];
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          {measures.map((m) => (
            <div key={m.name} className="rounded-md border bg-card p-3">
              <p className="text-sm font-medium">{m.name}</p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl font-semibold">{m.projected}%</span>
                <span className="text-xs text-muted-foreground">
                  projected · target {m.target}%
                </span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Current {m.current}% · {m.delta >= 0 ? "+" : ""}
                {m.delta} pts
              </div>
              <ProgressBar value={Math.min(100, m.projected)} />
            </div>
          ))}
        </div>
      );
    }

    case "connections-behavioral-care": {
      const referrals = num(data.referrals);
      const connected = num(data.connected);
      const rate = num(data.connectionRate);
      const days = num(data.avgDaysToConnect);
      return (
        <div className="grid gap-2 sm:grid-cols-4">
          <Stat label="Referrals" value={referrals.toLocaleString()} />
          <Stat label="Connected" value={connected.toLocaleString()} />
          <Stat label="Rate" value={`${rate}%`} />
          <Stat label="Avg days" value={`${days}d`} />
        </div>
      );
    }

    case "connections-primary-care": {
      const noPcp = num(data.membersNoPcpOverYear);
      const newConn = num(data.newPcpConnections);
      const rate = num(data.connectionRate);
      return (
        <div className="grid gap-2 sm:grid-cols-3">
          <Stat
            label="Members without PCP > 1yr"
            value={noPcp.toLocaleString()}
          />
          <Stat label="New PCP connections" value={newConn.toLocaleString()} />
          <Stat label="Connection rate" value={`${rate}%`} />
        </div>
      );
    }

    case "clinical-care-outcomes": {
      const months = (data.months as string[]) ?? [];
      const phq9 = asNumberArray(data.phq9Series);
      const gad7 = asNumberArray(data.gad7Series);
      const minimal = num(data.minimalThreshold);
      return (
        <div className="space-y-3">
          <TrendLine
            label="PHQ-9 (depression)"
            months={months}
            values={phq9}
            threshold={minimal}
            thresholdLabel="Minimal"
          />
          <TrendLine
            label="GAD-7 (anxiety)"
            months={months}
            values={gad7}
            threshold={minimal}
            thresholdLabel="Minimal"
          />
        </div>
      );
    }

    case "bh-medical-diagnoses": {
      const bh =
        (data.breakdown as Array<{ label: string; pct: number }>) ?? [];
      const med = (data.medical as Array<{ label: string; pct: number }>) ?? [];
      return (
        <div className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <BreakdownList title="Behavioral health" rows={bh} />
            <BreakdownList title="Medical" rows={med} />
          </div>
          {Boolean(data.whatWeFound) && (
            <p className="rounded-md bg-muted/40 p-3 text-sm">
              <span className="font-medium">What we found:</span>{" "}
              {String(data.whatWeFound)}
            </p>
          )}
        </div>
      );
    }

    case "harp-enrollment": {
      const statuses =
        (data.statuses as Array<{ label: string; value: number }>) ?? [];
      return (
        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
            {statuses.map((s) => (
              <Stat key={s.label} label={s.label} value={s.value.toLocaleString()} />
            ))}
          </div>
          {Boolean(data.howWeTrack) && (
            <p className="rounded-md bg-muted/40 p-3 text-xs italic text-muted-foreground">
              How we track HARP status — {String(data.howWeTrack)}
            </p>
          )}
        </div>
      );
    }

    case "gap-closures": {
      const statuses = (data.statuses as string[]) ?? [];
      const matrix =
        (data.matrix as Array<{ screening: string; values: number[] }>) ?? [];
      return (
        <div className="space-y-3">
          <StatusMatrix
            rowLabel="Screening"
            columns={statuses}
            rows={matrix.map((r) => ({ label: r.screening, values: r.values }))}
          />
          {Boolean(data.howWeTrack) && (
            <p className="rounded-md bg-muted/40 p-3 text-xs italic text-muted-foreground">
              How we track gap closures — {String(data.howWeTrack)}
            </p>
          )}
        </div>
      );
    }

    case "home-visits": {
      const statuses = (data.statuses as string[]) ?? [];
      const rows =
        (data.rows as Array<{ partner: string; values: number[] }>) ?? [];
      const totals = asNumberArray(data.totals);
      const withTotals =
        totals.length === statuses.length
          ? [...rows, { partner: "Total", values: totals }]
          : rows;
      return (
        <div className="space-y-3">
          <StatusMatrix
            rowLabel="Partner"
            columns={statuses}
            rows={withTotals.map((r) => ({ label: r.partner, values: r.values }))}
          />
          {Boolean(data.howWeTrack) && (
            <p className="rounded-md bg-muted/40 p-3 text-xs italic text-muted-foreground">
              How we track home visits — {String(data.howWeTrack)}
            </p>
          )}
        </div>
      );
    }

    case "member-spotlight": {
      const needs = (data.needs as string[]) ?? [];
      const priorities = (data.ongoingPriorities as string[]) ?? [];
      const challenge = (data.challenge as string[]) ?? [];
      const help = (data.howReemaHelped as string[]) ?? [];
      const outcomes = (data.outcomes as string[]) ?? [];
      const legacyStory =
        typeof data.story === "string" ? (data.story as string) : "";
      return (
        <div className="space-y-3 rounded-md bg-secondary/30 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20 text-lg font-semibold text-primary">
              {String(data.name ?? "?").slice(0, 1)}
            </div>
            <div>
              <p className="text-sm font-semibold">
                {String(data.headline ?? data.name ?? "")}
              </p>
              {Boolean(data.age) && (
                <p className="text-xs text-muted-foreground">
                  {String(data.name ?? "")} · {String(data.age ?? "")} years old
                </p>
              )}
            </div>
          </div>
          {Boolean(data.summary) && (
            <p className="text-sm leading-relaxed">{String(data.summary)}</p>
          )}
          {legacyStory && !data.summary && (
            <p className="text-sm leading-relaxed">{legacyStory}</p>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            {needs.length > 0 && <SpotlightList title="Needs" items={needs} />}
            {priorities.length > 0 && (
              <SpotlightList title="Ongoing priorities" items={priorities} />
            )}
            {challenge.length > 0 && (
              <SpotlightList title="The challenge" items={challenge} />
            )}
            {help.length > 0 && (
              <SpotlightList title="How Reema made the difference" items={help} />
            )}
          </div>
          {outcomes.length > 0 && (
            <SpotlightList title="Outcomes" items={outcomes} />
          )}
        </div>
      );
    }

    case "closing-value": {
      const rows =
        (data.costReductionRows as Array<{
          leftLabel: string;
          leftValue: number;
          rightLabel: string;
          rightValue: number;
          resultLabel: string;
          resultValue: number;
        }>) ?? [];
      const legacyMath =
        (data.costReductionMath as Array<{ label: string; value: number }>) ??
        [];
      const total = num(data.total);
      const validated = num(data.validatedOutcomes);
      return (
        <div className="space-y-3">
          {Boolean(data.summary) && (
            <p className="text-sm leading-relaxed text-muted-foreground">
              {String(data.summary)}
            </p>
          )}
          {rows.length > 0 ? (
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div
                  key={i}
                  className="grid grid-cols-1 gap-2 rounded-md border bg-card p-3 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-center"
                >
                  <MathCell label={r.leftLabel} value={r.leftValue.toLocaleString()} />
                  <span className="text-center text-lg font-semibold text-muted-foreground">→</span>
                  <MathCell label={r.rightLabel} value={r.rightValue.toLocaleString()} />
                  <span className="text-center text-lg font-semibold text-muted-foreground">=</span>
                  <MathCell
                    label={r.resultLabel}
                    value={`$${(r.resultValue / 1000).toFixed(0)}K`}
                    accent
                  />
                </div>
              ))}
            </div>
          ) : legacyMath.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-2">
              {legacyMath.map((m) => (
                <Stat
                  key={m.label}
                  label={m.label}
                  value={`$${m.value.toLocaleString()}`}
                />
              ))}
            </div>
          ) : null}
          <div className="rounded-md bg-primary/10 p-4">
            <p className="text-xs uppercase tracking-wide text-primary">
              {String(data.combinedLabel ?? "Combined projected savings")}
            </p>
            <p className="mt-1 text-3xl font-semibold">
              ${total.toLocaleString()}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            Validated outcomes to date: {validated}
          </p>
        </div>
      );
    }

    case "whats-possible-at-maturity": {
      const metrics =
        (data.metrics as Array<{ label: string; value: string }>) ?? [];
      return (
        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
            {metrics.map((m) => (
              <KpiTile key={m.label} label={m.label} value={m.value} />
            ))}
          </div>
          {Boolean(data.note) && (
            <p className="text-sm text-muted-foreground">{String(data.note)}</p>
          )}
          {Boolean(data.citationUrl) && (
            <p className="text-xs">
              <a {...generateDatadogTrackingProps('section-renderer-a-1')}
                href={String(data.citationUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                {String(data.citationLabel ?? "Source")}
              </a>
            </p>
          )}
        </div>
      );
    }

    case "looking-ahead": {
      return (
        <div className="space-y-2 text-sm">
          <p>
            <span className="font-medium">Next cycle focus:</span>{" "}
            {String(data.nextCycleFocus ?? "")}
          </p>
          {Boolean(data.closing) && (
            <p className="text-muted-foreground">{String(data.closing)}</p>
          )}
          {Boolean(data.preparedBy) && (
            <p className="pt-3 text-xs text-muted-foreground">
              Prepared by {String(data.preparedBy)}
            </p>
          )}
        </div>
      );
    }

    default:
      console.warn(`SectionRenderer: unknown sectionId "${sectionId}"`);
      return (
        <p className="text-sm italic text-muted-foreground">
          Unknown section — check the section library.
        </p>
      );
  }
}

// --- Insight pair --------------------------------------------------------

function InsightPair({ insights }: { insights: SectionInsights }) {
  const items = [
    insights.clinical ? { kind: "clinical" as const, item: insights.clinical } : null,
    insights.financial ? { kind: "financial" as const, item: insights.financial } : null,
  ].filter((x): x is { kind: "clinical" | "financial"; item: NonNullable<SectionInsights["clinical"]> } => x !== null);

  if (items.length === 0) return null;

  return (
    <div
      className={`grid gap-3 ${items.length > 1 ? "md:grid-cols-2" : ""}`}
    >
      {items.map(({ kind, item }) => (
        <aside
          key={kind}
          className={`rounded-md border p-3 text-sm ${
            kind === "clinical"
              ? "border-l-4 border-l-primary bg-primary/5"
              : "border-l-4 border-l-emerald-500 bg-emerald-500/5"
          }`}
        >
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {item.heading}
          </p>
          <p className="leading-relaxed">{item.body}</p>
          {item.citationUrl && (
            <a {...generateDatadogTrackingProps('section-renderer-a-2')}
              href={item.citationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs text-primary underline underline-offset-2"
            >
              {item.citationLabel ?? "Source"}
            </a>
          )}
        </aside>
      ))}
    </div>
  );
}

// --- Tiny visual primitives (no chart library) ---------------------------

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-md border bg-card p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function KpiTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold">{value}</p>
    </div>
  );
}

function ProgressBar({ value, label }: { value: number; label?: string }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div>
      {label && <p className="mb-1 text-xs text-muted-foreground">{label}</p>}
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function BarSpark({ values, label }: { values: number[]; label?: string }) {
  const max = Math.max(1, ...values);
  return (
    <div>
      {label && <p className="mb-1 text-xs text-muted-foreground">{label}</p>}
      <div className="flex h-16 items-end gap-1">
        {values.map((v, i) => (
          <div
            key={i}
            className="flex-1 rounded-t bg-primary/80"
            style={{ height: `${(v / max) * 100}%` }}
            title={String(v)}
          />
        ))}
      </div>
    </div>
  );
}

function Funnel({
  steps,
}: {
  steps: Array<{ label: string; value: number; subCopy?: string }>;
}) {
  const max = Math.max(1, ...steps.map((s) => s.value));
  return (
    <div className="space-y-2">
      {steps.map((s, i) => {
        const pct = Math.max(6, Math.round((s.value / max) * 100));
        const prior = i > 0 ? steps[i - 1].value : null;
        const dropoff =
          prior && prior > 0
            ? Math.round(((prior - s.value) / prior) * 100)
            : null;
        return (
          <div key={s.label}>
            <div className="flex items-baseline justify-between text-xs">
              <span className="font-medium">{s.label}</span>
              <span className="text-muted-foreground">
                {s.value.toLocaleString()}
                {dropoff !== null && ` · −${dropoff}%`}
              </span>
            </div>
            <div
              className="mt-1 h-6 rounded bg-primary/80"
              style={{ width: `${pct}%` }}
            />
            {s.subCopy && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {s.subCopy}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EngagementTrend({
  series,
}: {
  series: Array<{ month: string; engaged: number; interacted: number }>;
}) {
  if (series.length === 0) return null;
  const w = 640;
  const h = 220;
  const padL = 40;
  const padR = 12;
  const padT = 16;
  const padB = 28;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const max = Math.max(
    1,
    ...series.map((s) => Math.max(s.engaged, s.interacted)),
  );
  const stepX = series.length > 1 ? innerW / (series.length - 1) : innerW;
  const y = (v: number) => padT + innerH - (v / max) * innerH;
  const x = (i: number) => padL + i * stepX;
  const pathFor = (key: "engaged" | "interacted") =>
    series
      .map((s, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(s[key])}`)
      .join(" ");
  const midnight = "var(--reema-midnight-100, #1f2b47)";
  const coral = "var(--reema-coral-100, #e0654a)";
  const gridTicks = 4;
  return (
    <div className="rounded-md border bg-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--reema-coral-100,#e0654a)]">
          Engagement over the last 6 months
        </p>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-3 rounded-sm"
              style={{ background: midnight }}
            />
            Ever Interacted
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-3 rounded-sm"
              style={{ background: coral }}
            />
            Ever Engaged
          </span>
        </div>
      </div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="h-[220px] w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label="Engagement trend"
      >
        {Array.from({ length: gridTicks + 1 }, (_, i) => {
          const gy = padT + (innerH / gridTicks) * i;
          const val = Math.round(max - (max / gridTicks) * i);
          return (
            <g key={i}>
              <line
                x1={padL}
                x2={w - padR}
                y1={gy}
                y2={gy}
                stroke="currentColor"
                strokeOpacity="0.08"
              />
              <text
                x={padL - 6}
                y={gy + 3}
                textAnchor="end"
                fontSize="10"
                fill="currentColor"
                opacity="0.5"
              >
                {val.toLocaleString()}
              </text>
            </g>
          );
        })}
        <path
          d={pathFor("interacted")}
          fill="none"
          stroke={midnight}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={pathFor("engaged")}
          fill="none"
          stroke={coral}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {series.map((s, i) => (
          <g key={s.month}>
            <circle cx={x(i)} cy={y(s.interacted)} r={3} fill={midnight} />
            <circle cx={x(i)} cy={y(s.engaged)} r={3} fill={coral} />
            <text
              x={x(i)}
              y={h - 8}
              textAnchor="middle"
              fontSize="11"
              fill="currentColor"
              opacity="0.6"
            >
              {s.month}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}


function BreakdownList({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ label: string; pct: number }>;
}) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      <div className="space-y-1.5 text-sm">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="flex justify-between text-xs">
              <span>{r.label}</span>
              <span className="text-muted-foreground">{r.pct}%</span>
            </div>
            <ProgressBar value={r.pct} />
          </div>
        ))}
      </div>
    </div>
  );
}

function RankedList({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ label: string; count: number }>;
}) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      <ol className="space-y-1 text-sm">
        {rows.map((r, i) => (
          <li
            key={r.label}
            className="flex items-center justify-between border-b border-dashed py-1"
          >
            <span>
              <span className="mr-2 font-mono text-xs text-muted-foreground">
                {i + 1}.
              </span>
              {r.label}
            </span>
            <span className="text-muted-foreground">
              {r.count.toLocaleString()}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function TrendLine({
  label,
  months,
  values,
  threshold,
  thresholdLabel,
}: {
  label: string;
  months: string[];
  values: number[];
  threshold: number;
  thresholdLabel: string;
}) {
  const max = Math.max(threshold + 4, ...values, 1);
  return (
    <div>
      <p className="mb-1 text-xs font-medium">{label}</p>
      <div className="relative h-24 rounded border bg-card p-2">
        {/* threshold band */}
        <div
          className="absolute inset-x-2 rounded bg-emerald-500/10"
          style={{
            bottom: 8,
            height: `${(threshold / max) * 100}%`,
          }}
          title={thresholdLabel}
        />
        <div className="relative flex h-full items-end gap-2">
          {values.map((v, i) => (
            <div key={i} className="flex flex-1 flex-col items-center gap-1">
              <div
                className="w-full rounded-t bg-primary"
                style={{ height: `${(v / max) * 100}%` }}
                title={`${months[i] ?? ""}: ${v}`}
              />
              <span className="text-[10px] text-muted-foreground">
                {months[i] ?? ""}
              </span>
            </div>
          ))}
        </div>
      </div>
      <p className="mt-1 text-[10px] text-muted-foreground">
        Green band = {thresholdLabel} range (≤ {threshold}). Lower is better.
      </p>
    </div>
  );
}

function StatusMatrix({
  rowLabel,
  columns,
  rows,
}: {
  rowLabel: string;
  columns: string[];
  rows: Array<{ label: string; values: number[] }>;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="py-2 pr-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {rowLabel}
            </th>
            {columns.map((c) => (
              <th
                key={c}
                className="py-2 pr-3 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.label}
              className={
                r.label === "Total"
                  ? "border-b font-semibold"
                  : "border-b border-dashed"
              }
            >
              <td className="py-1.5 pr-3">{r.label}</td>
              {r.values.map((v, i) => (
                <td key={i} className="py-1.5 pr-3 text-right tabular-nums">
                  {v.toLocaleString()}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SpotlightList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      <ul className="space-y-1 text-sm">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MathCell({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className={accent ? "rounded-md bg-primary/10 p-2" : ""}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={
          accent
            ? "mt-0.5 text-xl font-semibold text-primary"
            : "mt-0.5 text-xl font-semibold"
        }
      >
        {value}
      </p>
    </div>
  );
}

function num(v: unknown): number {
  return typeof v === "number" && !Number.isNaN(v) ? v : 0;
}

function asNumberArray(v: unknown): number[] {
  return Array.isArray(v) ? v.filter((x): x is number => typeof x === "number") : [];
}
